import { doc, setDoc } from "firebase/firestore";
import { db } from "../../firebase";

/**
 * Clear all punchout-related client state.
 * Consolidates cleanup logic that was previously duplicated in transferCart.
 *
 * @param {Object} options
 * @param {Function} options.dispatch - Context dispatch function
 * @param {Function} options.clearCart - Cart hook's clearCart function
 * @param {string} [options.userId] - User ID for Firestore cleanup
 * @param {boolean} [options.preserveUi=false] - If true, only clear persisted state
 */
export function clearPunchoutClientState({ dispatch, clearCart, userId, preserveUi = false }) {
    if (!preserveUi) {
        // Clear punchout session from global context
        dispatch({ type: 'CLEAR_PUNCHOUT_SESSION' });
        dispatch({ type: 'CLEAR_CART' });

        // Clear local cart state
        if (clearCart) {
            clearCart();
        }
    }

    // Clear punchout session from sessionStorage
    sessionStorage.removeItem('punchoutSessionId');
    sessionStorage.removeItem('punchoutType');
    sessionStorage.removeItem('punchoutReturnUrl');

    // Clear cart from localStorage (it's persisted there too)
    const uid = userId || localStorage.getItem('uid');
    try {
        if (uid) {
            localStorage.removeItem(uid + 'cart');
        }
    } catch (e) {
        console.warn('Could not clear localStorage cart:', e);
    }
    
    // Clear cart from Firestore (async, fire-and-forget)
    // This prevents cart restoration on next visit
    if (uid) {
        clearFirestoreCart(uid).catch(e => {
            console.warn('Could not clear Firestore cart:', e);
        });
    }
}

/**
 * Clear cart from Firestore user document.
 * @param {string} uid - User ID
 */
async function clearFirestoreCart(uid) {
    const userDocRef = doc(db, "users", uid);
    await setDoc(userDocRef, { cart: {} }, { merge: true });
}

/**
 * Attempt to logout user before external redirect.
 * Logs warning on failure but continues (redirect is more important).
 *
 * @param {Function} logout - Auth logout function
 * @returns {Promise<boolean>} True if logout succeeded
 */
export async function maybeLogoutBeforeRedirect(logout) {
    try {
        await logout();
        return true;
    } catch (err) {
        console.warn('Logout warning:', err.message);
        return false;
    }
}
